/**
 * The /channels/:platform loaders that combine reads (spec §2.2), on the
 * browser client (the caller's RLS). Both THROW on a failed read, and the hook
 * turns that into 'failed'. A failed facts read fails its boxes and never shows
 * smaller totals (R9). A failed status read is "unknown" (R6), never "not
 * connected". The entities, the ads and the GA4 tab's ad-spend line are single
 * reads; the hook calls their readers directly.
 *
 * @module services/channels/channelLoaders
 */
import { fetchSyncStatuses } from '@/services/dashboard/syncStatusService';
import { todayDataDate } from '@/services/dataDate';
import { freshnessOf } from '@/services/looker/lookerAppData';
import type { OverviewWindows } from '@/services/overview/overviewTypes';
import { readChannelFacts } from './channelReaders';
import type { ChannelConnections, ChannelFactPair, ChannelPlatform } from './channelTypes';

/** R1: the period and the equal period before it, read in parallel; the cut windows are client filters over these. */
export const loadChannelFactPair = async (
  workspaceIds: readonly string[], platform: ChannelPlatform, w: OverviewWindows,
): Promise<ChannelFactPair> => {
  const [current, previous] = await Promise.all([
    readChannelFacts(workspaceIds, platform, w.current),
    readChannelFacts(workspaceIds, platform, w.previous),
  ]);
  return { current, previous };
};

/**
 * R6: one workspace's live accounts. fetchSyncStatuses already leaves demo
 * fixtures out, as Home does. One status read yields both halves:
 * - `platforms` = every platform with an account, analytics included, without
 *   repeats, in the order first seen;
 * - `freshness` = D1's pill rule (freshnessOf over todayDataDate(), exactly as
 *   lookerAppLoaders' loadChannelFreshness), which leaves analytics out.
 * Throws on a failed read, as lookerAppLoaders' readStatuses does.
 */
export const loadChannelConnections = async (workspaceId: string): Promise<ChannelConnections> => {
  const res = await fetchSyncStatuses(workspaceId);
  if (!res.success || !res.data) throw new Error(`channel connections read failed: ${res.error ?? 'no statuses'}`);
  const { statuses } = res.data;
  return {
    freshness: freshnessOf(statuses, todayDataDate()),
    // A Set keeps insertion order: each platform sits where it first appears.
    platforms: [...new Set(statuses.map((s) => s.platform))],
  };
};
